import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Typography, Box, Button } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useParams } from 'react-router';

import { ReceiptDocumentItem } from '../../types/property.types';
import { addReceiptDocuments, getPresignedUrl, uploadToS3 } from '../../api';

import FileSection from './file-section';
import styles from './registration-received-section.module.css';

import DrapAndDrop from '@/features/apps/components/drop-and-drap';
import { StatusFile } from '@/lib/enums';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { useSnackbar } from '@/hooks/use-snackbar';
import { MESSAGES } from '@/lib/constants';
import { PaperBoxStyles } from '@/components/styles';

// Helper predicates to reduce nesting - moved outside component
const isFileFailed = (file: FileItem): boolean => file.status === StatusFile.FAILED;
const isFileDone = (file: FileItem): boolean => file.status === StatusFile.DONE;
const createIsFileNotUploaded =
  (fileDoneItems: ReceiptDocumentItem[]) =>
  (file: FileItem): boolean =>
    !fileDoneItems.some((doneItem) => doneItem.title === file.fileName);

// Helper functions for file updates to reduce map complexity
const createFileUpdateFunction =
  (fileName: string, updates: Partial<FileItem>) =>
  (file: FileItem): FileItem =>
    file.fileName === fileName ? { ...file, ...updates } : file;

const updateFileWithProgress = (progress: number): Partial<FileItem> => ({
  status: progress === 100 ? StatusFile.DONE : StatusFile.LOADING,
  percent: progress,
});

const updateFileWithFailedStatus = (): Partial<FileItem> => ({
  status: StatusFile.FAILED,
});

// Helper functions for URL and upload operations
const createPresignedUrlRequest =
  (propertyId: string) =>
  ({ uniqueFileName }: { uniqueFileName: string }) =>
    getPresignedUrl(propertyId, uniqueFileName, 'upload');

const createUploadRequest =
  (
    filesToUpload: { file: File; uniqueFileName: string }[],
    handleUploadSingleFileWithNewName: (
      file: File,
      fileName: string,
      url: string
    ) => Promise<unknown>
  ) =>
  (url: { url: string }, index: number) =>
    handleUploadSingleFileWithNewName(
      filesToUpload[index].file,
      filesToUpload[index].uniqueFileName,
      url.url
    );

interface RegistrationReceivedSectionProps {
  readonly initialReceiptDocuments: ReceiptDocumentItem[];
  readonly handleFetchInitialReceiptDocuments: () => Promise<void>;
  readonly onDocumentsUploaded?: () => void;
}

interface FileItem {
  readonly file: File;
  readonly fileName: string;
  readonly size: number;
  readonly percent: number;
  readonly status: StatusFile;
}

export const RegistrationReceivedSection: React.FC<RegistrationReceivedSectionProps> = ({
  initialReceiptDocuments,
  handleFetchInitialReceiptDocuments,
  onDocumentsUploaded,
}) => {
  const { propertyId } = useParams<{ propertyId: string }>();
  const { setIsLoading } = useLoadingProgress();
  const { setSnackbarMessage } = useSnackbar();
  // Note: Staff loading is handled by the parent component (use-property-registration-form.ts)
  const [files, setFiles] = useState<FileItem[]>([]);
  const [fileDoneItems, setFileDoneItems] = useState<ReceiptDocumentItem[]>([]);
  const { setConfirmDialog } = useConfirmDialog();
  const isFileNotUploaded = createIsFileNotUploaded(fileDoneItems);
  const isHaveFileFailed = files.some(isFileFailed);
  const allowedExtensions = useMemo(
    () => [
      'pdf',
      'PDF',
      'xls',
      'XLS',
      'xlsx',
      'XLSX',
      'xlsm',
      'XLSM',
      'docx',
      'DOCX',
      'xdw',
      'XDW',
      'doc',
      'DOC',
    ],
    []
  );

  useEffect(() => {
    setFileDoneItems(
      files.filter(isFileDone).map((file) => ({
        classification: 1,
        title: file.fileName,
        remarks: '',
      }))
    );
  }, [files]);

  const getUniqueFileName = (
    fileName: string,
    existingFiles: FileItem[],
    existingDocuments: ReceiptDocumentItem[]
  ): string => {
    const existingFileNames = new Set([
      ...existingFiles.map((f) => f.fileName),
      ...existingDocuments.map((d) => d.title),
    ]);

    if (!existingFileNames.has(fileName)) {
      return fileName;
    }

    // Split filename and extension
    const lastDotIndex = fileName.lastIndexOf('.');
    const name = lastDotIndex >= 0 ? fileName.substring(0, lastDotIndex) : fileName;
    const extension = lastDotIndex >= 0 ? fileName.substring(lastDotIndex) : '';

    let counter = 1;
    let newFileName = `${name} (${counter})${extension}`;

    while (existingFileNames.has(newFileName)) {
      counter++;
      newFileName = `${name} (${counter})${extension}`;
    }

    return newFileName;
  };

  // Helper function to validate file extensions
  const validateFileExtensions = useCallback(
    (incomingFiles: File[]): File[] => {
      return incomingFiles.filter((file) => {
        const ext = file.name.split('.').pop();
        return !!ext && allowedExtensions.includes(ext);
      });
    },
    [allowedExtensions]
  );

  // Helper function to prepare files for upload
  const prepareFilesForUpload = useCallback(
    (
      filteredFiles: File[],
      oldFiles: FileItem[]
    ): { newFileItems: FileItem[]; filesToUpload: { file: File; uniqueFileName: string }[] } => {
      const newFileItems: FileItem[] = [];
      const filesToUpload: { file: File; uniqueFileName: string }[] = [];

      for (const file of filteredFiles) {
        const uniqueFileName = getUniqueFileName(
          file.name,
          [...oldFiles, ...newFileItems],
          initialReceiptDocuments
        );

        newFileItems.push({
          file,
          fileName: uniqueFileName,
          size: file.size,
          percent: 0,
          status: StatusFile.LOADING,
        });

        filesToUpload.push({ file, uniqueFileName });
      }

      return { newFileItems, filesToUpload };
    },
    [initialReceiptDocuments]
  );

  // Helper function to update file status
  const updateFileStatus = (
    fileName: string,
    progress: number,
    prevFiles: FileItem[]
  ): FileItem[] => {
    const updateFunction = createFileUpdateFunction(fileName, updateFileWithProgress(progress));
    return prevFiles.map(updateFunction);
  };

  // Helper function to mark file as failed
  const markFileAsFailed = (fileName: string, prevFiles: FileItem[]): FileItem[] => {
    const updateFunction = createFileUpdateFunction(fileName, updateFileWithFailedStatus());
    return prevFiles.map(updateFunction);
  };

  const handleUploadSingleFileWithNewName = useCallback(
    (currentFile: File, uniqueFileName: string, url: string) => {
      if (url) {
        return uploadToS3(url, currentFile, (progress: number) => {
          setFiles((prevFiles) => updateFileStatus(uniqueFileName, progress, prevFiles));
        }).catch(() => {
          setFiles((prevFiles) => markFileAsFailed(uniqueFileName, prevFiles));
        });
      } else {
        setFiles((prevFiles) => markFileAsFailed(uniqueFileName, prevFiles));
        return Promise.resolve();
      }
    },
    []
  );

  // Helper function to get presigned URLs for files
  const getPresignedUrlsForFiles = useCallback(
    async (filesToUpload: { file: File; uniqueFileName: string }[], propertyId: string) => {
      const urlPromises = filesToUpload.map(createPresignedUrlRequest(propertyId));
      return Promise.all(urlPromises);
    },
    []
  );

  // Helper function to upload files to S3
  const uploadFilesToS3 = useCallback(
    async (urls: { url: string }[], filesToUpload: { file: File; uniqueFileName: string }[]) => {
      const uploadPromises = urls.map(
        createUploadRequest(filesToUpload, handleUploadSingleFileWithNewName)
      );
      return Promise.all(uploadPromises);
    },
    [handleUploadSingleFileWithNewName]
  );

  const handleUploadFiles = useCallback(
    async (incomingFiles: File[]) => {
      const filteredFiles = validateFileExtensions(incomingFiles);

      if (filteredFiles.length !== incomingFiles.length) {
        setConfirmDialog({
          title:
            'このファイル形式はサポートされていません。アップロード可能な形式：pdf, xls, xlsx, xlsm, docx, xdw',
        });
        return;
      }

      if (!propertyId) {
        return;
      }

      const oldFiles = files;
      const { newFileItems, filesToUpload } = prepareFilesForUpload(filteredFiles, oldFiles);

      setFiles(oldFiles.concat(newFileItems));

      const urls = await getPresignedUrlsForFiles(filesToUpload, propertyId);
      await uploadFilesToS3(urls, filesToUpload);
      // Notify parent that documents have been uploaded
      if (onDocumentsUploaded) {
        onDocumentsUploaded();
      }
    },
    [
      files,
      propertyId,
      validateFileExtensions,
      prepareFilesForUpload,
      getPresignedUrlsForFiles,
      uploadFilesToS3,
      setConfirmDialog,
      onDocumentsUploaded,
    ]
  );

  const handleDropUpload = useCallback(
    (files: File[]) => {
      void handleUploadFiles(files);
    },
    [handleUploadFiles]
  );

  const handleRemoveFile = useCallback(
    (fileName: string) => {
      setFiles(files.filter((file) => file.fileName !== fileName));
    },
    [files]
  );

  const handleUploadSingleFile = useCallback((currentFile: File, url: string) => {
    return uploadToS3(url, currentFile, (progress: number) => {
      setFiles((prevFiles) => updateFileStatus(currentFile.name, progress, prevFiles));
    }).catch(() => {
      setFiles((prevFiles) => markFileAsFailed(currentFile.name, prevFiles));
    });
  }, []);

  const handleReUploadFile = useCallback(
    async (fileName: string) => {
      if (!propertyId) {
        return;
      }
      const currentFile = files.find((file) => file.fileName === fileName);
      if (!currentFile) {
        return;
      }
      const url = await getPresignedUrl(propertyId, fileName, 'upload');
      await handleUploadSingleFile(currentFile.file, url.url);
    },
    [files, propertyId, handleUploadSingleFile]
  );

  const handleFormSubmit = useCallback(async () => {
    if (!propertyId) {
      return;
    }
    setIsLoading(true);
    try {
      await addReceiptDocuments(propertyId, fileDoneItems);
      setFiles((prevFiles) => prevFiles.filter(isFileNotUploaded));
      setSnackbarMessage(MESSAGES.REGISTER_RECEIPT_SUCCESS);
      // Notify parent that documents have been uploaded and saved
      if (onDocumentsUploaded) {
        onDocumentsUploaded();
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : '受領資料登録に失敗しました。もう一度お試しください。';

      setConfirmDialog({
        title: message,
      });
    } finally {
      await handleFetchInitialReceiptDocuments();
      setIsLoading(false);
    }
  }, [
    handleFetchInitialReceiptDocuments,
    fileDoneItems,
    propertyId,
    isFileNotUploaded,
    setConfirmDialog,
    onDocumentsUploaded,
  ]);
  return (
    <PaperBoxStyles elevation={1} sx={{ mb: 3, paddingBottom: '32px' }} className={styles.paper}>
      <Typography variant='h5' fontWeight='bold' className={styles.title}>
        受領資料登録
      </Typography>
      <Typography variant='body2' color='text.secondary' className={styles.subtitle}>
        資料アップロード
      </Typography>

      <Box className={styles.filesContainer}>
        <DrapAndDrop
          handleDropUpload={handleDropUpload}
          className={styles.dropZone}
          isHaveFileFailed={isHaveFileFailed}
          allowedExtensions={allowedExtensions}
        />
      </Box>

      {files.length > 0 && (
        <Box className={styles.filesList}>
          {files.map((file) => (
            <FileSection
              key={file.fileName}
              fileName={file.fileName}
              file={file.file}
              percent={file.percent}
              status={file.status}
              handleRemoveFile={handleRemoveFile}
              handleReUploadFile={(fileName) => {
                void handleReUploadFile(fileName);
              }}
            />
          ))}
        </Box>
      )}

      <Box className={styles.buttonContainer}>
        <Button
          disabled={fileDoneItems.length === 0}
          startIcon={<CheckIcon className={styles.checkIcon} />}
          className={`${styles.uploadButton} ${fileDoneItems.length === 0 && styles.uploadButtonDisabled}`}
          onClick={() => {
            void handleFormSubmit();
          }}
        >
          アップロードする
        </Button>
      </Box>
    </PaperBoxStyles>
  );
};
